import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, MapPin, Clock, ArrowRight, ChevronLeft, ChevronRight, Heart, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import SectionHeading from '../components/SectionHeading';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Event categories with colors
const categories = {
  JUMMAH: { label: 'JUMMAH', bgColor: 'bg-primary/20', textColor: 'text-primary', borderColor: 'border-primary/30' },
  RAMADAN: { label: 'RAMADAN', bgColor: 'bg-secondary/20', textColor: 'text-secondary', borderColor: 'border-secondary/30' },
  IFTAR: { label: 'IFTAR', bgColor: 'bg-orange-500/20', textColor: 'text-orange-400', borderColor: 'border-orange-500/30' },
  COMMUNITY: { label: 'COMMUNITY', bgColor: 'bg-purple-500/20', textColor: 'text-purple-400', borderColor: 'border-purple-500/30' },
  EID: { label: 'EID', bgColor: 'bg-green-500/20', textColor: 'text-green-400', borderColor: 'border-green-500/30' },
  EDUCATION: { label: 'EDUCATION', bgColor: 'bg-blue-500/20', textColor: 'text-blue-400', borderColor: 'border-blue-500/30' },
};

// All upcoming events
const allEvents = [
  // Friday Jummah Events
  {
    id: 'jummah-feb-13',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-02-13',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  // Ramadan starts Feb 17
  {
    id: 'taraweeh-feb-17',
    category: 'RAMADAN',
    title: 'Isha & Taraweeh',
    description: 'First night of Ramadan. Join us for Isha and Taraweeh prayers.',
    date: '2026-02-17',
    time: '7:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'taraweeh-feb-18',
    category: 'RAMADAN',
    title: 'Isha & Taraweeh',
    description: 'Nightly Taraweeh prayers during Ramadan.',
    date: '2026-02-18',
    time: '7:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'taraweeh-feb-19',
    category: 'RAMADAN',
    title: 'Isha & Taraweeh',
    description: 'Nightly Taraweeh prayers during Ramadan.',
    date: '2026-02-19',
    time: '7:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'jummah-feb-20',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-02-20',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'iftar-feb-21',
    category: 'IFTAR',
    title: 'Community Iftar',
    description: 'Break your fast with the community. Iftar at Maghrib time.',
    date: '2026-02-21',
    time: '5:56 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'taraweeh-feb-22',
    category: 'RAMADAN',
    title: 'Isha & Taraweeh',
    description: 'Nightly Taraweeh prayers during Ramadan.',
    date: '2026-02-22',
    time: '7:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'taraweeh-feb-23',
    category: 'RAMADAN',
    title: 'Isha & Taraweeh',
    description: 'Nightly Taraweeh prayers during Ramadan.',
    date: '2026-02-23',
    time: '7:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'taraweeh-feb-24',
    category: 'RAMADAN',
    title: 'Isha & Taraweeh',
    description: 'Nightly Taraweeh prayers during Ramadan.',
    date: '2026-02-24',
    time: '7:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'taraweeh-feb-25',
    category: 'RAMADAN',
    title: 'Isha & Taraweeh',
    description: 'Nightly Taraweeh prayers during Ramadan.',
    date: '2026-02-25',
    time: '7:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'taraweeh-feb-26',
    category: 'RAMADAN',
    title: 'Isha & Taraweeh',
    description: 'Nightly Taraweeh prayers during Ramadan.',
    date: '2026-02-26',
    time: '7:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'jummah-feb-27',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-02-27',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'iftar-feb-28',
    category: 'IFTAR',
    title: 'Community Iftar',
    description: 'Break your fast with the community. Iftar at Maghrib time.',
    date: '2026-02-28',
    time: '6:06 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'taraweeh-mar-01',
    category: 'RAMADAN',
    title: 'Isha & Taraweeh',
    description: 'Nightly Taraweeh prayers during Ramadan.',
    date: '2026-03-01',
    time: '7:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'taraweeh-mar-02',
    category: 'RAMADAN',
    title: 'Isha & Taraweeh',
    description: 'Nightly Taraweeh prayers during Ramadan.',
    date: '2026-03-02',
    time: '7:45 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'taraweeh-mar-03',
    category: 'RAMADAN',
    title: 'Isha & Taraweeh',
    description: 'Nightly Taraweeh prayers during Ramadan.',
    date: '2026-03-03',
    time: '7:45 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'taraweeh-mar-04',
    category: 'RAMADAN',
    title: 'Isha & Taraweeh',
    description: 'Nightly Taraweeh prayers during Ramadan.',
    date: '2026-03-04',
    time: '7:45 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'taraweeh-mar-05',
    category: 'RAMADAN',
    title: 'Isha & Taraweeh',
    description: 'Nightly Taraweeh prayers during Ramadan.',
    date: '2026-03-05',
    time: '7:45 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'jummah-mar-06',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-03-06',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'iftar-mar-08',
    category: 'IFTAR',
    title: 'Community Iftar',
    description: 'Break your fast with the community. Iftar at Maghrib time. DST begins.',
    date: '2026-03-08',
    time: '6:16 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'taraweeh-mar-09',
    category: 'RAMADAN',
    title: 'Isha & Taraweeh',
    description: 'Nightly Taraweeh prayers during Ramadan.',
    date: '2026-03-09',
    time: '8:00 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'taraweeh-mar-10',
    category: 'RAMADAN',
    title: 'Isha & Taraweeh',
    description: 'Nightly Taraweeh prayers during Ramadan.',
    date: '2026-03-10',
    time: '8:00 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'taraweeh-mar-11',
    category: 'RAMADAN',
    title: 'Isha & Taraweeh',
    description: 'Nightly Taraweeh prayers during Ramadan.',
    date: '2026-03-11',
    time: '8:00 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'taraweeh-mar-12',
    category: 'RAMADAN',
    title: 'Isha & Taraweeh',
    description: 'Nightly Taraweeh prayers during Ramadan.',
    date: '2026-03-12',
    time: '8:00 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'jummah-mar-13',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-03-13',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'iftar-mar-14',
    category: 'IFTAR',
    title: 'Community Iftar',
    description: 'Break your fast with the community. Iftar at Maghrib time.',
    date: '2026-03-14',
    time: '6:24 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'taraweeh-mar-15',
    category: 'RAMADAN',
    title: 'Isha & Taraweeh',
    description: 'Nightly Taraweeh prayers during Ramadan.',
    date: '2026-03-15',
    time: '8:00 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'taraweeh-mar-16',
    category: 'RAMADAN',
    title: 'Isha & Taraweeh',
    description: 'Nightly Taraweeh prayers during Ramadan.',
    date: '2026-03-16',
    time: '8:00 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'taraweeh-mar-17',
    category: 'RAMADAN',
    title: 'Isha & Taraweeh',
    description: 'Nightly Taraweeh prayers during Ramadan.',
    date: '2026-03-17',
    time: '8:00 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'taraweeh-mar-18',
    category: 'RAMADAN',
    title: 'Isha & Taraweeh',
    description: 'Last night of Ramadan Taraweeh prayers.',
    date: '2026-03-18',
    time: '8:00 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'jummah-mar-20',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-03-20',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'jummah-mar-27',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-03-27',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  // April Jummah prayers
  {
    id: 'jummah-apr-03',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-04-03',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'jummah-apr-10',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-04-10',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'jummah-apr-17',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-04-17',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'jummah-apr-24',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-04-24',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  // May Jummah prayers
  {
    id: 'jummah-may-01',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-05-01',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'jummah-may-08',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-05-08',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'jummah-may-15',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-05-15',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'jummah-may-22',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-05-22',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'jummah-may-29',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-05-29',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  // June Jummah prayers
  {
    id: 'jummah-jun-05',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-06-05',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'jummah-jun-12',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-06-12',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'jummah-jun-19',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-06-19',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'jummah-jun-26',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-06-26',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  // July Jummah prayers
  {
    id: 'jummah-jul-03',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-07-03',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'jummah-jul-10',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-07-10',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'jummah-jul-17',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-07-17',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'jummah-jul-24',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-07-24',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'jummah-jul-31',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-07-31',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  // August Jummah prayers
  {
    id: 'jummah-aug-07',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-08-07',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'jummah-aug-14',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-08-14',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'jummah-aug-21',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-08-21',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'jummah-aug-28',
    category: 'JUMMAH',
    title: 'Weekly Jummah Prayer',
    description: 'Join us for the Friday congregation. Khutbah starts at 1:30 PM.',
    date: '2026-08-28',
    time: '1:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  // Community Events
  {
    id: 'potluck-apr-26',
    category: 'COMMUNITY',
    title: 'Community Potluck',
    description: 'Join us for a community potluck dinner. Bring a dish to share with fellow community members.',
    date: '2026-04-26',
    time: '5:30 PM - 7:30 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'eid-adha-may-26',
    category: 'EID',
    title: 'Eid al-Adha Prayer',
    description: 'Join us for Eid al-Adha prayer. Takbeer starts at 8:45 AM, prayer at 9:00 AM.',
    date: '2026-05-26',
    time: '9:00 AM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'bbq-may-31',
    category: 'COMMUNITY',
    title: 'TNMC Community BBQ',
    description: 'Join us for the TNMC Community BBQ! Good food, great people, stronger community. Fun activities including bouncing castle, face painting, cotton candy, and more!',
    date: '2026-05-31',
    time: '3:00 PM - 7:00 PM',
    location: 'St. Paul Parking Lot, 54 St. Paul Crescent, Barrie',
  },
  {
    id: 'movie-night-jun-27',
    category: 'COMMUNITY',
    title: 'Family Movie Night',
    description: 'Family-friendly movie night with snacks and refreshments. Bring your blankets!',
    date: '2026-06-27',
    time: '5:30 PM - 9:00 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  // Food Bank Drive - Ongoing
  {
    id: 'food-drive-ongoing',
    category: 'COMMUNITY',
    title: 'TNMC Food Bank Drive',
    description: 'Feed Hope, Share Kindness! Collecting food items for Barrie Food Bank every Friday, Saturday & Sunday. Items accepted: Rice, Pasta, Cooking Oil, Snacks & Drinks.',
    date: '2026-04-25',
    time: 'Every Fri, Sat, Sun',
    location: '54 St. Paul Crescent, Barrie',
  },
  // Education Schedule - Term 1
  {
    id: 'edu-term1-start',
    category: 'EDUCATION',
    title: 'Term 1 Starts',
    description: 'First day of Islamic Weekend School - Spring Term begins.',
    date: '2026-04-04',
    time: '3:00 PM - 5:00 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'edu-midterm1',
    category: 'EDUCATION',
    title: 'Mid-Term Exams',
    description: 'Mid-term examinations for all students.',
    date: '2026-05-16',
    time: '3:00 PM - 5:00 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'edu-final1',
    category: 'EDUCATION',
    title: 'Final Exams - Term 1',
    description: 'Final examinations for Term 1.',
    date: '2026-06-20',
    time: '3:00 PM - 5:00 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'edu-term1-end',
    category: 'EDUCATION',
    title: 'Term 1 Ends - Summer Break',
    description: 'Last day of Term 1. Summer break begins June 29.',
    date: '2026-06-28',
    time: '3:00 PM - 5:00 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'edu-term2-start',
    category: 'EDUCATION',
    title: 'Term 2 Starts',
    description: 'First day of Fall Term - Classes resume after summer break.',
    date: '2026-08-08',
    time: '3:00 PM - 5:00 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'edu-midterm2',
    category: 'EDUCATION',
    title: 'Mid-Term Exams - Term 2',
    description: 'Mid-term examinations for all students.',
    date: '2026-10-03',
    time: '3:00 PM - 5:00 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'edu-final2',
    category: 'EDUCATION',
    title: 'Final Exams - Term 2',
    description: 'Final examinations for Term 2.',
    date: '2026-11-21',
    time: '3:00 PM - 5:00 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'edu-musabaqa',
    category: 'EDUCATION',
    title: 'Annual Quran Musabaqa',
    description: 'Annual Quran recitation competition for all students.',
    date: '2026-11-28',
    time: '3:00 PM - 5:00 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
  {
    id: 'edu-term2-end',
    category: 'EDUCATION',
    title: 'Term 2 Ends - Winter Break',
    description: 'Last day of Term 2. Winter break begins November 30.',
    date: '2026-11-29',
    time: '3:00 PM - 5:00 PM',
    location: '54 St. Paul Crescent, Barrie',
  },
];

// Helper function to filter upcoming events (today and future) in EST timezone
const getUpcomingEvents = (events) => {
  // Get current date in EST timezone
  const now = new Date();
  const estOffset = -5 * 60; // EST is UTC-5
  const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);
  const estTime = new Date(utcTime + (estOffset * 60000));
  const todayEST = estTime.toISOString().split('T')[0];
  
  return events.filter(event => {
    return event.date >= todayEST;
  });
};

// Check if event is today (in EST)
const isEventToday = (dateStr) => {
  const now = new Date();
  const estOffset = -5 * 60;
  const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);
  const estTime = new Date(utcTime + (estOffset * 60000));
  const todayEST = estTime.toISOString().split('T')[0];
  return dateStr === todayEST;
};

const EventCard = ({ event }) => {
  const category = categories[event.category];
  const eventDate = new Date(event.date + 'T12:00:00');
  const isToday = isEventToday(event.date);
  
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`p-6 rounded-2xl bg-card border ${isToday ? 'border-green-500 ring-2 ring-green-500/20' : category.borderColor} hover:border-opacity-100 transition-all duration-300 relative`}
      data-testid={`event-card-${event.id}`}
    >
      {isToday && (
        <div className="absolute -top-3 left-4 px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full">
          TODAY
        </div>
      )}
      <div className="flex items-center justify-between mb-4">
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${category.bgColor} ${category.textColor} border ${category.borderColor}`}>
          {category.label}
        </span>
        <span className="text-sm text-muted-foreground flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {formatDate(eventDate)}
        </span>
      </div>
      
      <h3 className="text-lg font-semibold font-['Outfit'] text-foreground mb-2">
        {event.title}
      </h3>
      
      <p className="text-sm text-muted-foreground mb-4">
        {event.description}
      </p>
      
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span className="flex items-center gap-1">
          <Clock className="h-3 w-3 text-primary" />
          {event.time} EST
        </span>
      </div>
      
      <div className="flex items-start gap-1 mt-2 text-sm text-muted-foreground">
        <MapPin className="h-3 w-3 text-primary flex-shrink-0 mt-0.5" />
        <span>{event.location}</span>
      </div>
    </motion.div>
  );
};

const EventsPage = () => {
  const [filter, setFilter] = useState('ALL');
  const [events, setEvents] = useState(allEvents);

  useEffect(() => {
    axios.get(`${API}/content/events`)
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          setEvents(res.data);
        }
      })
      .catch(() => {/* fall back to hardcoded */});
  }, []);

  // Get only upcoming events (today and future), then sort by date
  const upcomingEvents = getUpcomingEvents(events);
  const sortedEvents = [...upcomingEvents].sort((a, b) => new Date(a.date) - new Date(b.date));
  
  // Filter events by category
  const filteredEvents = filter === 'ALL' 
    ? sortedEvents 
    : sortedEvents.filter(e => e.category === filter);

  return (
    <div className="min-h-screen pt-24">
      {/* Hero */}
      <section className="py-16 lg:py-24 hero-gradient islamic-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              Mark Your Calendar
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground font-['Outfit'] leading-tight mb-6">
              Upcoming <span className="text-gradient">Events</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Join us for Friday Jummah prayers and special Ramadan 1447 AH programs. 
              May Allah accept our fasting and prayers. Ameen.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Ramadan Banner */}
      <section className="py-8 bg-gradient-to-r from-secondary/20 via-background to-primary/20 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <h2 className="text-2xl font-bold font-['Outfit'] text-foreground">
                Ramadan 1447 AH Program Schedule
              </h2>
              <p className="text-muted-foreground">February 17 - March 18, 2026</p>
            </div>
            <a 
              href="https://chat.whatsapp.com/BZpW0tw4MUuLAVuZmzBRDU?mode=gi_t"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="rounded-full bg-green-500 hover:bg-green-600 text-white">
                <MessageCircle className="mr-2 h-4 w-4" />
                Join WhatsApp for Updates
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="py-8 bg-card/50 border-b border-border sticky top-20 z-30 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground mr-2">Filter:</span>
            <button
              onClick={() => setFilter('ALL')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                filter === 'ALL' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-accent text-muted-foreground hover:text-foreground'
              }`}
              data-testid="filter-all"
            >
              All Events
            </button>
            {Object.entries(categories).map(([key, cat]) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  filter === key 
                    ? `${cat.bgColor} ${cat.textColor} border ${cat.borderColor}` 
                    : 'bg-accent text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`filter-${key.toLowerCase()}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Events Grid */}
      <section className="py-16 lg:py-24" data-testid="events-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Events count */}
          <div className="mb-8 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {filteredEvents.length} upcoming event{filteredEvents.length !== 1 ? 's' : ''}
            </p>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              <span>Live schedule</span>
            </div>
          </div>

          {filteredEvents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredEvents.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-card rounded-2xl border border-border">
              <Calendar className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No Upcoming Events</h3>
              <p className="text-muted-foreground mb-4">
                {filter === 'ALL' 
                  ? 'All scheduled events have passed. Check back soon for new events!' 
                  : `No upcoming ${categories[filter]?.label || filter} events. Try a different filter.`}
              </p>
              {filter !== 'ALL' && (
                <Button onClick={() => setFilter('ALL')} variant="outline" className="rounded-full">
                  View All Events
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Academic Calendar Section */}
      <section className="py-16 lg:py-24 bg-card border-y border-border" data-testid="academic-calendar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="Weekend Islamic School"
            title="Academic Calendar 2026"
            description="Key dates for our Islamic education program"
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Term 1 */}
            <div className="p-6 rounded-2xl bg-background border border-primary/30">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Calendar className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Term 1 - Spring</h3>
                  <p className="text-sm text-primary">April - June 2026</p>
                </div>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Term Starts</span>
                  <span className="text-foreground font-medium">April 4</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Mid-Term Exams</span>
                  <span className="text-foreground font-medium">May 16-17</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Final Exams</span>
                  <span className="text-foreground font-medium">June 20-21</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Term Ends</span>
                  <span className="text-foreground font-medium">June 28</span>
                </div>
              </div>
              <div className="mt-4 p-3 rounded-lg bg-primary/5 border border-primary/20">
                <p className="text-xs text-primary font-medium">Summer Break: June 29 - August 7</p>
              </div>
            </div>

            {/* Term 2 */}
            <div className="p-6 rounded-2xl bg-background border border-secondary/30">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <Calendar className="h-5 w-5 text-secondary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Term 2 - Fall</h3>
                  <p className="text-sm text-secondary">August - November 2026</p>
                </div>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Term Starts</span>
                  <span className="text-foreground font-medium">August 8</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Mid-Term Exams</span>
                  <span className="text-foreground font-medium">October 3-4</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Final Exams</span>
                  <span className="text-foreground font-medium">November 21-22</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Quran Musabaqa</span>
                  <span className="text-foreground font-medium">November 28-29</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Term Ends</span>
                  <span className="text-foreground font-medium">November 29</span>
                </div>
              </div>
              <div className="mt-4 p-3 rounded-lg bg-secondary/5 border border-secondary/20">
                <p className="text-xs text-secondary font-medium">Winter Break: Nov 30 - Jan 2, 2027</p>
              </div>
            </div>

            {/* Term 3 */}
            <div className="p-6 rounded-2xl bg-background border border-blue-500/30">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Calendar className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Term 3 - Winter</h3>
                  <p className="text-sm text-blue-400">January - February 2027</p>
                </div>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Term Starts</span>
                  <span className="text-foreground font-medium">January 3</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Mid-Term Exams</span>
                  <span className="text-foreground font-medium">January 31 - Feb 1</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Final Exams</span>
                  <span className="text-foreground font-medium">February 21-22</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Term Ends</span>
                  <span className="text-foreground font-medium">February 28</span>
                </div>
              </div>
              <div className="mt-4 p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                <p className="text-xs text-blue-400 font-medium">Ramadan Break: March 2027</p>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground">
              Classes held Saturday & Sunday, 3:00 PM - 5:00 PM EST at 54 St. Paul Crescent, Barrie
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-card islamic-pattern" data-testid="events-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionHeading
            badge="Support Ramadan Programs"
            title="Help Us Serve the Community"
            description="Your donations help us provide iftar meals, support orphans, and maintain our programs during Ramadan."
          />
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/donate" data-testid="events-donate-btn">
              <Button size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-8 glow-teal">
                <Heart className="mr-2 h-5 w-5" />
                Donate Now
              </Button>
            </Link>
            <Link to="/volunteer" data-testid="events-volunteer-btn">
              <Button size="lg" variant="outline" className="rounded-full px-8">
                Volunteer for Ramadan
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
